import React, { useState, useEffect } from "react";
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useNavigate } from 'react-router-dom';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { jwtDecode } from 'jwt-decode'; // Correct import for jwt-decode
import infoIcon from '../assets/icons/Info.svg';

// Define interface for the API response data
interface UserData {
  id: number;
  userName: string;
  email: string;
  phoneNumber: number | null;
  azureToken: string | null;
  roleId: number;
  createdDate: string;
  updatedDate: string | null;
  status: boolean;
  isRegisteredUser: boolean;
}

interface CustomJwtPayload {
  RoleId: string; // Treat RoleId as string
}

const ManageUser: React.FC = () => {
  const navigate = useNavigate();

  // States for date range picker
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [userData, setUserData] = useState<UserData[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchData, setSearchData] = useState('');
  const [suggestions, setSuggestions] = useState<UserData[]>([]);

  const handleUserClick = (userId: string) => {
    navigate(`/user-details/${userId}`);
  };

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      toast.error("No auth token found. Please log in.");
      navigate('/');
    } else {
      try {
        const decodedToken = jwtDecode<CustomJwtPayload>(token);
        if (decodedToken.RoleId !== "1") { // Compare RoleId as string
          toast.error("Unauthorized access: RoleId is not 1");
          navigate('/');
        }
      } catch (error) {
        toast.error("Invalid token. Please log in again.");
        navigate('/');
      }
    }
  }, [navigate]);

  // Format date to the required API format (MM-DD-YYYY)
  const formatDate = (date: Date | null) => {
    if (!date) return "";
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const year = date.getFullYear();
    return `${year}-${month}-${day}`;
  };



  // Fetch user data based on date range
  const fetchUserData = async (fromDate: string, toDate: string) => {
    setIsLoading(true);
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('No auth token found');
      }

      const response = await fetch(`https://pamsapi.vofoxsolutions.com/api/Admin/GetUsersByDateRangeAsync?fromDate=${fromDate}&toDate=${toDate}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch user data: ${response.status}`);
      }

      const data: UserData[] = await response.json();
      setUserData(data);

      if (data.length === 0) {
        if (!toast.isActive("no-users")) {
          toast.info("No users were registered in the selected date range.", { toastId: "no-users" });
        }
      }

    } catch (error) {
      // toast.error("An error occurred while fetching user data.");
    } finally {
      setIsLoading(false);
    }
  };

  // Handle fetching data when the user clicks 'Fetch Data'
  // const handleFetchData = () => {
  //   const token = localStorage.getItem('token');
  //   if (token) {
  //     try {
  //       const decodedToken = jwtDecode<CustomJwtPayload>(token);
  //       if (decodedToken.RoleId !== "1") { // Compare RoleId as string
  //         toast.error("Unauthorized access: RoleId is not 1");
  //         return;
  //       }
  //     } catch (error) {
  //       console.error('Error decoding token:', error);
  //       toast.error("Invalid token. Please log in again.");
  //       return;
  //     }
  //   } else {
  //     toast.error("No auth token found. Please log in.");
  //     return;
  //   }


  //   const fromDate = formatDate(startDate);
  //   const toDate = formatDate(endDate);

  //   if (!fromDate || !toDate) {
  //     toast.info("Please select both start and end date.");
  //     return;
  //   }
  //   if (startDate && endDate && startDate > endDate) {
  //     toast.error("Start date must be earlier than or equal to end date.");
  //     return;
  //   }

  //   fetchUserData(fromDate, toDate);
  // };
  const handleFetchData = () => {
    if (!startDate || !endDate) {
        if (!toast.isActive("select-dates")) {
            toast.info("Please select both start and end date.", { toastId: "select-dates" });
        }
        return;
    }

    if (startDate > endDate) {
        if (!toast.isActive("invalid-dates")) {
            toast.error("Start date must be earlier than or equal to end date.", { toastId: "invalid-dates" });
        }
        return;
    }

    setIsLoading(true);
    fetchUserData(formatDate(startDate), formatDate(endDate));
};


  // Handle search input change
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.toLowerCase(); // Convert to lowercase for case-insensitive search
    setSearchData(value);

    if (value.length > 0) {
      // Filter userData based on the email or name entered in the search box
      const filteredSuggestions = userData.filter(user =>
        user.email.toLowerCase().includes(value)
      );

      setSuggestions(filteredSuggestions);
    } else {
      setSuggestions([]);
    }
  };

  const handleSearchClick = () => {
    if (searchData.length > 0) {
      const filteredSuggestions = userData.filter(user =>
        user.email.toLowerCase().includes(searchData)
      );
      setSuggestions(filteredSuggestions);
    }else{
      setSuggestions([]);
    }
  };
  // Handle suggestion click
  const handleSuggestionClick = (userId: string) => {
    setSearchData(userId);
    setSuggestions([]);
    navigate(`/user-details/${userId}`);
  };

  // Add these handler functions
  // const handleStartDateChange = (date: Date | null) => {
  //   setStartDate(date);
  //   // If end date exists and is before new start date, clear it
  //   if (endDate && date && endDate < date) {
  //     setEndDate(null);
  //   }
  // };

  // const handleEndDateChange = (date: Date | null) => {
  //   setEndDate(date);
  // };

  return (
    <div className="h-screen overflow-hidden bg-magnolia">
      <div className="flex flex-col h-screen">
        <header className="flex items-center justify-between px-6 py-3 bg-white">
          <div>
            <h1 className="text-lg font-semibold text-gray-700">Manage Users</h1>
          </div>
        </header>

        <main className="flex-1 overflow-x-hidden overflow-y-auto">
          <div className="container px-6 py-6 mx-auto">
            <h4 className="text-base sm:text-lg text-dark font-semibold">
              User Management Statistics
              {userData.length > 0 && (
                <span className="text-xl text-blue-600 ml-2 font-bold">
                  (Total users in the date range: {userData.length})
                </span>
              )}
            </h4>

            <div className="flex gap-4 mt-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Start Date</label>
                <DatePicker
                  selected={startDate}
                  onChange={(date) => {
                    setStartDate(date);
                    setEndDate(null);
                }}                  
                selectsStart
                startDate={startDate}
                endDate={endDate}
                maxDate={new Date()}
                dateFormat="yyyy-MM-dd"
                className="border rounded p-2"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">End Date</label>
                <DatePicker
                 selected={endDate}
                 onChange={(date) => setEndDate(date)}
                 selectsEnd
                 startDate={startDate}
                 endDate={endDate}
                 minDate={startDate || undefined}
                 maxDate={new Date()}
                 dateFormat="yyyy-MM-dd"
                 className="border rounded p-2"
                 disabled={!startDate}
                />
              </div>

              <button
                onClick={handleFetchData}
                className="bg-indigo-600 text-white px-4 h-10 rounded-md place-self-end"
              >
                Fetch Data
              </button>

              <div className="relative bg-white rounded-md flex items-center h-fit self-end ml-24">
                <input
                  type="text"
                  placeholder="Search user by email..."
                  className="flex-grow pl-4 pr-2 py-2 rounded-md focus:outline-none border-none placeholder-gray-500 w-full sm:w-auto"
                  value={searchData}
                  onChange={handleInputChange}
                />
                <button
                  className="flex items-center px-4 py-2 bg-blue-600 text-white text-sm rounded-r-md"
                  onClick={handleFetchData}
                >
                  <svg
                    className="w-5 h-5 mr-2"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M21 21L15 15M17 10C17 13.866 13.866 17 10 17C6.13401 17 3 13.866 3 10C3 6.13401 6.13401 3 10 3C13.866 3 17 6.13401 17 10Z"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </button>

                {/* Suggestions dropdown */}
                {suggestions.length > 0 && (
                  <ul className="absolute top-10 left-0 w-fit bg-white border border-gray-200 rounded-md shadow-lg z-10 max-h-60 overflow-y-auto">
                        {suggestions.map((suggestion) => (
                          <li
                            key={suggestion.id}
                            className="p-2 cursor-pointer hover:bg-gray-100"
                            onClick={() => handleSuggestionClick(suggestion.id.toString())}
                          >
                            {suggestion.email}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>
    
                {/* Loading Spinner */}
                {isLoading && <p className="mt-4 text-gray-500">Loading data...</p>}
    
                {/* User Data */}
                {userData.length > 0 && (
                  <div className="mt-6">
                    <h4 className="text-lg font-semibold">
                      User Data (From {formatDate(startDate)} to {formatDate(endDate)})
                    </h4>
    
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mt-4">
                      {userData.map((user) => (
                        <div key={user.id} className="p-6 bg-white rounded-lg shadow-md"
                        onClick={() => handleUserClick(user.id.toString())}
                        >
                          <p className="text-sm font-semibold text-gray-600">
                            Username: <span className="text-lg font-bold text-blue-600">{user.userName}</span>
                          </p>
                          <p className="text-sm font-semibold text-gray-600">
                            Email: <span className="text-sm text-blue-600">{user.email}</span>
                          </p>
                          <p className="text-sm font-semibold text-gray-600">
                            Registered Date: <span className="text-sm text-blue-600">{user.createdDate}</span>
                          </p>
    
                          {/* Status and Edit button in a flex container */}
                          <div className="flex items-center justify-between mt-2">
                            <p className="text-sm font-semibold text-gray-600">
                              Status: <span className="text-sm text-green-600">{user.status ? 'Active' : 'Inactive'}</span>
                            </p>
                            {/* <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleinfo(user.id.toString()); // Pass the userId as string directly
                              }}
                              title="info"
                              className="p-1 focus:outline-none flex-shrink-0 hover:bg-gray-200 rounded-md"
                            >
                              <img src={infoIcon} alt="info" className="w-8 h-8" />
                            </button> */}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </main>
    
            <ToastContainer />
          </div>
        </div>
      );
    };
    
    export default ManageUser;
    